import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
    ANC_RADIO_GROUP,
    type AncRadioGroupHost
} from '../anc-radio-group/anc-radio-group.token';

/**
 * Design-system radio button (Figma node 190:6183).
 * Must be used inside `lib-anc-radio-group`. 24px hit area, 20px circle;
 * selected state uses a 5px ring fill; unselected uses a 2px outline.
 *
 * @example
 * <lib-anc-radio-group formControlName="orderType">
 *   <lib-anc-radio [value]="'shipment'" [label]="'Shipment'"></lib-anc-radio>
 * </lib-anc-radio-group>
 */
@Component({
    selector: 'lib-anc-radio',
    imports: [CommonModule, TranslateModule],
    templateUrl: './anc-radio.component.html',
    styleUrls: ['./anc-radio.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncRadioComponent {
    protected readonly group: AncRadioGroupHost = inject(ANC_RADIO_GROUP);

    /** Value emitted when this option is selected. */
    value = input.required<string | number | boolean>();

    /** Optional label text (run through the translate pipe). */
    label = input<string>('');

    /** Per-option disabled state (in addition to group-level disable). */
    disabled = input<boolean>(false);

    /** Emits this option's value when the user selects it. */
    readonly changed = output<string | number | boolean>();

    /** Whether this option is currently selected. */
    protected isChecked(): boolean {
        return this.group.value() === this.value();
    }

    /** Whether interaction is blocked (group or option disabled). */
    protected isDisabled(): boolean {
        return this.group.disabled() || this.disabled();
    }

    /** Select this option via the parent group. */
    protected onSelect(): void {
        if (this.isDisabled()) {
            return;
        }

        this.group.select(this.value());
        this.changed.emit(this.value());
    }
}
